import axios, { getAuthHeader } from './client.ts';
import services from './services.ts';

export const ENDPOINTS = {
  CHECK_USERNAME: `${services.PROFILE}/username`,
  LOGOUT: `${services.PROFILE}/token`,
  VERIFY_EMAIL: (token: string) => `${services.PROFILE}/email-confirmation/${token}`,
  RESEND_VERIFICATION_EMAIL: (userId: string) => `${services.PROFILE}/email-confirm/${userId}`,
  PROFILE: `${services.PROFILE}/profile`,
  PROFILE_BY_USERNAME: (username: string) => `${services.PROFILE}/profile/${username}?byUsername=true`,
  USER_DETAILS: `${services.PROFILE}/profile/` + '${userId}',
  UPDATE_PROFILE: (userId: string) => `${services.PROFILE}/profile/${userId}`,
  WISHLIST: (userId: string) => `${services.PROFILE}/profile/${userId}/wishlist`,
  WISHLIST_ITEM: (userId: string, itemId: string) => `${services.PROFILE}/profile/${userId}/wishlist/${itemId}`,
  WISHLIST_WITH_CURRENCY: (userId: string, currency: string) =>
    `${services.PROFILE}/profile/${userId}/wishlist?currency=${currency}`,
  WISHLIST_BY_USERNAME: (username: string, currency: string) =>
    `${services.PROFILE}/profile/${username}/wishlist?byUsername=true&currency=${currency}`,
  ADD_LINK: (userId: string) => `${services.PROFILE}/profile/${userId}/links`,
  UPDATE_LINK: (userId: string, linkId: string) => `${services.PROFILE}/profile/${userId}/links/${linkId}`,
  RANDOM_CREATOR: `${services.PROFILE}/random`,
  PHONE_CONFIRMATION: `${services.PROFILE}/phone-confirmation`,
  COMPLETE_PHONE_CONFIRMATION: (confirmationId: string) => `${services.PROFILE}/phone-confirmation/${confirmationId}`,
  UPDATE_PHONE_NUMBER: (userId: string) => `${services.PROFILE}/phone-confirmation/${userId}}`,
  STRIPE_QUEUE_STATUS: `${services.PROFILE}/signup-queue-status`,
};

export const checkUsername = (username: string) => axios.get(`${ENDPOINTS.CHECK_USERNAME}/${username.toLowerCase()}`);

export const createProfile = (data: Record<string, unknown>) =>
  axios.post(ENDPOINTS.PROFILE, data, {
    headers: { 'Content-Type': 'application/json' },
  });

export const verifyEmail = (token: string) => axios.put(ENDPOINTS.VERIFY_EMAIL(token), {});

export const resendVerificationEmail = (userId: string, token: string) =>
  axios.post(
    ENDPOINTS.RESEND_VERIFICATION_EMAIL(userId),
    {},
    {
      headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token) },
    },
  );

export const getProfile = (userId: string, token: string) =>
  axios.get(ENDPOINTS.USER_DETAILS.replace('${userId}', userId), {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token) },
  });

export const getProfileByUsername = (username: string, token: string) =>
  axios.get(ENDPOINTS.PROFILE_BY_USERNAME(username), {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token) },
  });

export const updateProfile = (data: Record<string, unknown>, userId: string, token: string) =>
  axios.put(ENDPOINTS.UPDATE_PROFILE(userId), data, {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token) },
  });

export const deleteProfile = (userId: string, token: string) =>
  axios.delete(ENDPOINTS.UPDATE_PROFILE(userId), {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token) },
  });

export const getWishlist = (userId: string, currency: string, token?: string) =>
  axios.get(ENDPOINTS.WISHLIST_WITH_CURRENCY(userId, currency), {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token!) },
  });

export const getWishlistByUsername = (username: string, currency: string, token?: string) =>
  axios.get(ENDPOINTS.WISHLIST_BY_USERNAME(username, currency), {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token!) },
  });

export const addWishlistItem = (data: Record<string, unknown>, userId: string, token: string) =>
  axios.post(ENDPOINTS.WISHLIST(userId), data, {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token) },
  });

export const updateWishlistItem = (data: Record<string, unknown>, userId: string, itemId: string, token: string) =>
  axios.put(ENDPOINTS.WISHLIST_ITEM(userId, itemId), data, {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token) },
  });

export const deleteWishlistItem = (userId: string, itemId: string, token: string) =>
  axios.delete(ENDPOINTS.WISHLIST_ITEM(userId, itemId), {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token) },
  });

export const addLink = (data: Record<string, unknown>, userId: string, token: string) =>
  axios.post(ENDPOINTS.ADD_LINK(userId), data, {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token) },
  });

export const updateLink = (data: Record<string, unknown>, userId: string, linkId: string, token: string) =>
  axios.put(ENDPOINTS.UPDATE_LINK(userId, linkId), data, {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token) },
  });

export const deleteLink = (userId: string, linkId: string, token: string) =>
  axios.delete(ENDPOINTS.UPDATE_LINK(userId, linkId), {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token) },
  });

export const getRandomCreator = (token?: string) =>
  axios.get(ENDPOINTS.RANDOM_CREATOR, {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token!) },
  });

export const phoneConfirmation = (data: Record<string, unknown>) =>
  axios.post(ENDPOINTS.PHONE_CONFIRMATION, data, {
    headers: { 'Content-Type': 'application/json' },
  });

export const resendPhoneConfirmation = (confirmationId: string, data: Record<string, unknown>) =>
  axios.post(ENDPOINTS.COMPLETE_PHONE_CONFIRMATION(confirmationId), data, {
    headers: { 'Content-Type': 'application/json' },
  });

export const completePhoneConfirmation = (confirmationId: string, data: Record<string, unknown>) =>
  axios.put(ENDPOINTS.COMPLETE_PHONE_CONFIRMATION(confirmationId), data, {
    headers: { 'Content-Type': 'application/json' },
  });

export const updatePhoneNumber = (userId: string, data: Record<string, unknown>, token: string) =>
  axios.patch(ENDPOINTS.UPDATE_PHONE_NUMBER(userId), data, {
    headers: { 'Content-Type': 'application/json' },
    ...getAuthHeader(token!),
  });

export const stripeQueueStatus = (token: string) =>
  axios.get(ENDPOINTS.STRIPE_QUEUE_STATUS, {
    headers: { ...{ 'Content-Type': 'application/json' }, ...getAuthHeader(token!) },
  });
